import { z } from 'zod';

export const SKILL_MODES = [
  'common',
  'generation',
  'chat',
  'modification',
  'casediscovery',
] as const;

export const SKILL_TYPES = [
  'template',
  'iteratif',
  'verification',
  'edit',
  'visualization',
  'custom',
] as const;

export const SKILL_SOURCES = ['hector', 'personal'] as const;
export const SKILL_CUSTOMIZATIONS = ['hector', 'overridden', 'personal'] as const;
export const SKILL_AVAILABILITIES = [
  'active',
  'disabled_by_user',
  'disabled_by_hector',
] as const;
export const SKILL_CHANGE_EFFECTS = [
  'create_personal',
  'create_override',
  'update_override',
  'update_personal',
  'reset_override',
  'delete_personal',
] as const;
export const SKILL_CHANGE_OPERATIONS = ['create', 'update', 'delete'] as const;
export const SKILL_CHANGE_STATUSES = ['success', 'declined', 'expired', 'error'] as const;

/**
 * Méthodes Hector qu'un compte ne peut ni remplacer ni désactiver : la consigne
 * système impose de les charger, elle suppose donc qu'elles existent et restent
 * actives. Cette liste protège l'écriture (`SkillRepository`, `ManageSkills`) ;
 * aucun outil n'en dépend à l'exécution.
 */
export const MANDATORY_HECTOR_SKILL_SLUGS = ['chiffrage-droit-social'] as const;
export const SOCIAL_CALCULATION_SKILL_SLUG = MANDATORY_HECTOR_SKILL_SLUGS[0];

export function isMandatoryHectorSkillSlug(
  slug: string,
): slug is (typeof MANDATORY_HECTOR_SKILL_SLUGS)[number] {
  return (MANDATORY_HECTOR_SKILL_SLUGS as readonly string[]).includes(slug);
}

export const SkillModeSchema = z.enum(SKILL_MODES);
export const SkillTypeSchema = z.enum(SKILL_TYPES);
export const SkillSourceSchema = z.enum(SKILL_SOURCES);
export const SkillCustomizationSchema = z.enum(SKILL_CUSTOMIZATIONS);
export const SkillAvailabilitySchema = z.enum(SKILL_AVAILABILITIES);
export const SkillChangeEffectSchema = z.enum(SKILL_CHANGE_EFFECTS);
export const SkillChangeOperationSchema = z.enum(SKILL_CHANGE_OPERATIONS);
export const SkillChangeStatusSchema = z.enum(SKILL_CHANGE_STATUSES);

export const SkillSlugSchema = z
  .string()
  .trim()
  .min(1, 'skills.slug must not be empty')
  .max(100, 'skills.slug must contain at most 100 characters')
  .regex(
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
    'skills.slug must be lowercase kebab-case without a leading or trailing hyphen',
  );

const SkillDescriptionSchema = z
  .string()
  .trim()
  .min(1, 'skills.description must not be empty');

export const SkillNameSchema = z
  .string()
  .trim()
  .min(1, 'skills.name must not be empty')
  // PostgreSQL VARCHAR counts Unicode code points, while String.length counts
  // UTF-16 units and would reject DB-valid names containing non-BMP characters.
  .refine((name) => Array.from(name).length <= 255, {
    message: 'skills.name must contain at most 255 characters',
  });

const SkillContentSchema = z
  .string()
  .trim()
  .min(1, 'skills.content must not be empty');

/** Metadata safe to expose in catalogs; prompt content stays load-on-demand. */
export const SkillMetadataSchema = z.object({
  slug: SkillSlugSchema,
  name: SkillNameSchema,
  description: SkillDescriptionSchema,
  type: SkillTypeSchema,
  mode: SkillModeSchema,
  isActive: z.boolean(),
  source: SkillSourceSchema,
});

/** Full skill contract returned only by explicit detail/load flows. */
export const SkillDetailSchema = SkillMetadataSchema.extend({
  content: SkillContentSchema,
});

/** Effective catalog metadata after resolving Hector defaults and user-owned rows. */
export const ResolvedSkillMetadataSchema = SkillMetadataSchema.extend({
  customization: SkillCustomizationSchema,
  availability: SkillAvailabilitySchema,
});

/** Effective full skill returned by resolved detail and mutation-result flows. */
export const ResolvedSkillDetailSchema = ResolvedSkillMetadataSchema.extend({
  content: SkillContentSchema,
});

export const SkillSettingsEntrySchema = z.object({
  skill: ResolvedSkillDetailSchema,
  customization: SkillCustomizationSchema,
  availability: SkillAvailabilitySchema,
  canRename: z.boolean(),
  canReset: z.boolean(),
  canDelete: z.boolean(),
}).superRefine((entry, context) => {
  // The duplicated top-level fields keep settings consumers simple, but they
  // must never drift from the authoritative resolved skill projection.
  if (entry.customization !== entry.skill.customization) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['customization'],
      message: `skill settings customization must match skill.customization '${entry.skill.customization}', got '${entry.customization}'`,
    });
  }
  if (entry.availability !== entry.skill.availability) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['availability'],
      message: `skill settings availability must match skill.availability '${entry.skill.availability}', got '${entry.availability}'`,
    });
  }
});

/** Strict payload accepted by the staff route when creating a Hector skill. */
export const GlobalSkillCreateSchema = SkillDetailSchema.pick({
  slug: true,
  name: true,
  description: true,
  type: true,
  mode: true,
  content: true,
}).strict();

/**
 * Strict staff patch. Every supplied value is catalog-safe; an empty patch is
 * rejected so the route cannot report a successful no-op mutation.
 */
export const GlobalSkillUpdateSchema = z.object({
  name: SkillMetadataSchema.shape.name.optional(),
  description: SkillMetadataSchema.shape.description.optional(),
  type: SkillMetadataSchema.shape.type.optional(),
  mode: SkillMetadataSchema.shape.mode.optional(),
  content: SkillDetailSchema.shape.content.optional(),
  is_active: z.boolean().optional(),
}).strict().refine((patch) => Object.keys(patch).length > 0, {
  message: 'skills global update must contain at least one editable field',
});

/** Editable fields. Ownership, type, mode and source are deliberately absent. */
export const PersonalSkillDraftSchema = z.object({
  slug: SkillSlugSchema,
  description: SkillDescriptionSchema,
  content: SkillContentSchema,
});

/**
 * Server-owned defaults materialized when a personal draft becomes a record.
 * Unknown caller fields are stripped by Zod before this boundary is consumed.
 */
export const PersonalSkillRecordSchema = PersonalSkillDraftSchema.extend({
  type: z.literal('custom').default('custom'),
  mode: z.literal('common').default('common'),
  source: z.literal('personal').default('personal'),
  isActive: z.boolean().default(true),
});

export const CreateSkillDraftSchema = z.object({
  operation: z.literal('create'),
  skill: PersonalSkillDraftSchema,
});

export const UpdateSkillDraftSchema = z.object({
  operation: z.literal('update'),
  currentSlug: SkillSlugSchema,
  skill: PersonalSkillDraftSchema,
});

export const DeleteSkillDraftSchema = z.object({
  operation: z.literal('delete'),
  skill: z.object({ slug: SkillSlugSchema }),
});

export const SkillChangeDraftSchema = z.discriminatedUnion('operation', [
  CreateSkillDraftSchema,
  UpdateSkillDraftSchema,
  DeleteSkillDraftSchema,
]);

const CorrelatedFrameSchema = z.object({
  requestId: z.string().uuid('skill change requestId must be a UUID'),
});

export const SkillChangeRequestSchema = z.discriminatedUnion('operation', [
  CreateSkillDraftSchema.extend({
    type: z.literal('skill_change_request'),
    requestId: CorrelatedFrameSchema.shape.requestId,
    effect: z.literal('create_personal'),
  }),
  UpdateSkillDraftSchema.extend({
    type: z.literal('skill_change_request'),
    requestId: CorrelatedFrameSchema.shape.requestId,
    effect: SkillChangeEffectSchema.extract([
      'create_override',
      'update_override',
      'update_personal',
    ]),
  }),
  DeleteSkillDraftSchema.extend({
    type: z.literal('skill_change_request'),
    requestId: CorrelatedFrameSchema.shape.requestId,
    effect: SkillChangeEffectSchema.extract(['reset_override', 'delete_personal']),
  }),
]);

export const SkillChangeResponseSchema = CorrelatedFrameSchema.extend({
  type: z.literal('skill_change_response'),
  accepted: z.boolean(),
  skill: PersonalSkillDraftSchema.optional(),
}).strict();

export const SkillChangeResultSchema = CorrelatedFrameSchema.extend({
  type: z.literal('skill_change_result'),
  operation: SkillChangeOperationSchema,
  status: SkillChangeStatusSchema,
  effect: SkillChangeEffectSchema.optional(),
  skill: ResolvedSkillDetailSchema.optional(),
  error: z.string().trim().min(1).optional(),
}).superRefine((result, context) => {
  if (result.status === 'error' && result.error === undefined) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['error'],
      message: `skill_change_result request '${result.requestId}' with status 'error' requires a concrete error`,
    });
  }
  // Before target resolution there is no consent card and therefore no
  // trustworthy effect. Every card-backed outcome must echo the server effect.
  if (
    result.effect === undefined
    && (result.status !== 'error' || result.skill !== undefined)
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['effect'],
      message: `skill_change_result request '${result.requestId}' with status '${result.status}' requires the server-resolved effect`,
    });
  }
});

export type SkillMode = z.infer<typeof SkillModeSchema>;
export type SkillType = z.infer<typeof SkillTypeSchema>;
export type SkillSource = z.infer<typeof SkillSourceSchema>;
export type SkillCustomization = z.infer<typeof SkillCustomizationSchema>;
export type SkillAvailability = z.infer<typeof SkillAvailabilitySchema>;
export type SkillChangeEffect = z.infer<typeof SkillChangeEffectSchema>;
export type SkillMetadata = z.infer<typeof SkillMetadataSchema>;
export type SkillDetail = z.infer<typeof SkillDetailSchema>;
export type ResolvedSkillMetadata = z.infer<typeof ResolvedSkillMetadataSchema>;
export type ResolvedSkillDetail = z.infer<typeof ResolvedSkillDetailSchema>;
export type SkillSettingsEntry = z.infer<typeof SkillSettingsEntrySchema>;
export type GlobalSkillCreate = z.infer<typeof GlobalSkillCreateSchema>;
export type GlobalSkillUpdate = z.infer<typeof GlobalSkillUpdateSchema>;
export type PersonalSkillDraft = z.infer<typeof PersonalSkillDraftSchema>;
export type PersonalSkillRecord = z.infer<typeof PersonalSkillRecordSchema>;
export type CreateSkillDraft = z.infer<typeof CreateSkillDraftSchema>;
export type UpdateSkillDraft = z.infer<typeof UpdateSkillDraftSchema>;
export type DeleteSkillDraft = z.infer<typeof DeleteSkillDraftSchema>;
export type SkillChangeDraft = z.infer<typeof SkillChangeDraftSchema>;
export type SkillChangeRequest = z.infer<typeof SkillChangeRequestSchema>;
export type SkillChangeResponse = z.infer<typeof SkillChangeResponseSchema>;
export type SkillChangeResult = z.infer<typeof SkillChangeResultSchema>;
